/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { SaleService } from '../services/sale-service';
import { salesKeys } from './sales-keys';
import type { SalePaperworkMilestoneType } from '../types/sale.types';

export interface SalePaperworkMilestoneTypes {
  isLoading: boolean;
  isError: boolean;
  types: SalePaperworkMilestoneType[];
  addType: (label: string, required: boolean) => Promise<void>;
  updateType: (
    milestoneId: string,
    patch: { label?: string; required?: boolean },
  ) => Promise<void>;
}

/**
 * The agency's configured paperwork milestone types — what a sale may
 * record a date against. A fresh agency reads the four this backend used
 * to hardcode; adding or editing here never removes or reorders anything
 * (there is no endpoint for either).
 *
 * @param agencyId - The owning agency.
 * @returns The view-model: the list, load state, and add/edit actions.
 */
export function useSalePaperworkMilestoneTypes(agencyId: string): SalePaperworkMilestoneTypes {
  const queryClient = useQueryClient();
  const enabled = Boolean(agencyId);

  const typesQuery = useQuery({
    queryKey: salesKeys.milestoneTypes(agencyId),
    queryFn: () => SaleService.listPaperworkMilestoneTypes(agencyId),
    enabled,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: salesKeys.milestoneTypes(agencyId) });

  const addMutation = useMutation({
    mutationFn: (args: { label: string; required: boolean }) =>
      SaleService.addPaperworkMilestoneType(agencyId, args),
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: (args: { milestoneId: string; patch: { label?: string; required?: boolean } }) =>
      SaleService.updatePaperworkMilestoneType(agencyId, args.milestoneId, args.patch),
    onSuccess: invalidate,
  });

  return {
    isLoading: typesQuery.isLoading,
    isError: typesQuery.isError,
    types: typesQuery.data ?? [],
    addType: async (label, required) => {
      await addMutation.mutateAsync({ label, required });
    },
    updateType: async (milestoneId, patch) => {
      await updateMutation.mutateAsync({ milestoneId, patch });
    },
  };
}
